import {
  ExclusivePriceSetup,
  ExclusivePriceSetupProps,
} from '@client/new/common/selfServeExclusive/exclusivePriceSetup';
import { Alert } from '@design-system/components/alert/alert';
import { Button, ButtonLink } from '@design-system/components/button/button';
import { ExternalLink } from '@design-system/components/link/externalLink';
import { SideModal } from '@design-system/components/modal/sideModal';
import { Typography } from '@design-system/components/typography/typography';
import { sprinkles } from '@design-system/sprinkles.css';
import { isEmpty } from 'lodash';
import { clsx } from 'clsx';
import { Divider } from '@mui/material';

const exclusivePriceHelpLink =
  'https://help.playbookapp.io/en/articles/9698798-setting-up-your-exclusive-subscription-formerly-called-pay-per-creator-ppc';

export const SubscriptionsSettingsModal = ({
  isOpen,
  close,
  errors,
  save,
  readonly,
  submitting,
  onHelpClick,
  promotionsPath,
  hideSave,
  ...setup
}: {
  isOpen: boolean;
  close: () => void;
  save: () => void;
  errors?: string[];
  readonly: boolean;
  submitting: boolean;
  onHelpClick: (url: string) => void;
  promotionsPath: string;
  hideSave: boolean;
} & ExclusivePriceSetupProps) => {
  return (
    <SideModal
      isOpen={isOpen}
      onClose={close}
      noSpacing
      testId="subscriptionSettingsModal"
    >
      <div className="flex flex-col gap-6 px-12 py-6 w-full">
        <div className="flex justify-between">
          <Typography variant="title">Settings</Typography>
          {hideSave ? null : (
            <Button color="neutral" onClick={save} loading={submitting}>
              Save
            </Button>
          )}
        </div>
        {isEmpty(errors) ? null : (
          <Alert
            variant="error"
            title="Can’t save, required fields or information are missing"
            details={errors}
            allowClose={true}
          />
        )}
        <div className="flex flex-col gap-4 w-full overflow-y-auto">
          <Typography variant="body" size="large">
            Subscription Price
          </Typography>
          {readonly ? null : (
            <Alert
              variant="info"
              title="Must know"
              details="Once you go live or start offering products in your subscription, price changes won’t be possible."
            />
          )}
          <div
            className={clsx(
              'flex flex-col md:flex-row justify-between p-4 w-full',
              sprinkles({ bg: 'W-100' }),
            )}
          >
            <ExclusivePriceSetup
              {...setup}
              sectionClassName="p-0"
              readonly={readonly}
              disabled={submitting}
            />
            {readonly ? null : (
              <div
                className={clsx(
                  'w-[300px] h-[200px] flex flex-col gap-2 p-4 rounded-[10px]',
                  sprinkles({
                    bg: 'N-10',
                  }),
                )}
              >
                <Typography variant="body" size="medium">
                  How to price a subscription?
                </Typography>
                <Typography variant="body" size="medium" fontWeight="normal">
                  Pricing is specific to you and the amount of effort you'll be
                  putting into your offering. We've compiled our best practices
                  and great ways to think about this in our Pricing guide.
                </Typography>
                <ExternalLink
                  label="Read more"
                  href={exclusivePriceHelpLink}
                  textVariant="body"
                  textSize="medium"
                  textColor="P-80"
                  onClick={() => onHelpClick(exclusivePriceHelpLink)}
                />
              </div>
            )}
          </div>
          {readonly ? (
            <>
              <Divider />
              <div className="flex flex-col gap-2">
                <div className="flex gap-2 justify-between items-center">
                  <Typography variant="body" size="large">
                    Did you know about adding discounts?
                  </Typography>
                  <ButtonLink
                    to={promotionsPath}
                    className="h-min"
                    color="primaryTonal"
                  >
                    Set Promotion
                  </ButtonLink>
                </div>
                <Typography variant="body" size="medium" fontWeight="normal">
                  You can run Sales and Promotions from the Promotions Tab
                </Typography>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </SideModal>
  );
};
